import { CanAccess } from '@modules/common/components/CanAccess';
import { AddIntegrationFormDrawer } from '@modules/integrationsV2/components/AddIntegrationFormDrawer';
import { SelectOrAddWorkspaceMenu } from '@modules/integrationsV2/components/SelectOrAddWorkspaceMenu';
import { useIsReauthIntegrationEnabled } from '@modules/integrationsV2/hooks';
import { useGetConnectedIntegrationWorkspaces } from '@modules/integrationsV2/hooks/useGetConnectedIntegrationWorkspaces';
import {
  getCanAddIntegrationChannelsMetadata,
  getCanReauthorizeIntegration,
  getOAuthRedirectionURL,
} from '@modules/integrationsV2/utils';
import { getAddIntegrationFormDrawerContent } from '@modules/integrationsV2/utils/getAddIntegrationFormDrawerContent';
import { SyncRounded } from '@mui/icons-material';
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import {
  IntegrationChannelViewFragment,
  IntegrationType,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { useState } from 'react';
import useGetIntegrationsFormData from 'v2/core/repository/edge-server/integrations/useGetIntegrationsFormData';
import { useGetUser } from 'v2/core/services/user';

interface ReauthorizeIntegrationActionPtops {
  integrationID: string | undefined;
  integrationChannel: IntegrationChannelViewFragment;
  integrationType: IntegrationType;
}

export const ReauthorizeIntegrationAction = ({
  integrationID,
  integrationChannel,
  integrationType,
}: ReauthorizeIntegrationActionPtops) => {
  const {
    tenant: { slug: tenantSlug },
  } = useGetUser();

  const isReauthIntegrationsEnabled = useIsReauthIntegrationEnabled();

  const [workspaceMenuAnchorEl, setWorkspaceMenuAnchorEl] =
    useState<HTMLElement | null>(null);

  const [
    shouldOpenReauthorizeIntegrationDrawer,
    setShouldOpenReauthorizeIntegrationDrawer,
  ] = useState(false);
  const [integrationIDToReauthorize, setIntegrationIDToReauthorize] = useState<
    string | undefined
  >(undefined);

  const { data: integrationsFormData } = useGetIntegrationsFormData({
    fetchPolicy: 'cache-first', // Because we have "cache-and-network" on the root component, we can use "cache-first" here.
  });

  const connectedWorkspaces = useGetConnectedIntegrationWorkspaces({
    integrationType,
    channelType: integrationChannel.type,
  });

  const selectedSourceFormData = integrationsFormData?.find(
    (integrationForm) =>
      integrationForm.channel.type === integrationChannel.type,
  );

  const oAuthURL =
    selectedSourceFormData?.__typename === 'OAuthFormData'
      ? selectedSourceFormData.oAuthURL
      : undefined;

  const canReauthorizeIntegration = getCanReauthorizeIntegration({
    channelType: integrationChannel.type,
  });

  if (!isReauthIntegrationsEnabled || !canReauthorizeIntegration) {
    return null;
  }

  const onClick = (event: React.MouseEvent<HTMLElement>) => {
    const canAddIntegrationChannelsMetadata =
      getCanAddIntegrationChannelsMetadata(integrationChannel.type);

    const shouldOpenWorkspaceMenu =
      !integrationID &&
      canAddIntegrationChannelsMetadata &&
      connectedWorkspaces.length > 0;

    if (shouldOpenWorkspaceMenu) {
      setWorkspaceMenuAnchorEl(event.currentTarget);
      return;
    }

    onReauthorizeIntegration(integrationID ?? '');
  };

  const onReauthorizeIntegration = (id: string) => {
    const drawerContentComponent = getAddIntegrationFormDrawerContent({
      integrationChannel,
      integrationType,
      onDrawerClose: () => {}, // Not required, because we are not gonna use it
    });

    if (drawerContentComponent) {
      setIntegrationIDToReauthorize(id);
      setShouldOpenReauthorizeIntegrationDrawer(true);
      return;
    }

    if (oAuthURL) {
      const redirectURL = getOAuthRedirectionURL({
        channelType: integrationChannel.type,
        oAuthURL,
        tenantSlug,
        integrationType,
        integrationID: id,
      });

      window.open(redirectURL, '_self');
    }
  };

  return (
    <>
      <CanAccess
        resource={PermissionResource.Integration}
        operation={PermissionOperation.Write}
      >
        <MenuItem onClick={onClick}>
          <ListItemIcon>
            <SyncRounded fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reauthorize</ListItemText>
        </MenuItem>
      </CanAccess>
      <SelectOrAddWorkspaceMenu
        integrationChannel={integrationChannel}
        integrationType={integrationType}
        oAuthURL={oAuthURL}
        anchorEl={workspaceMenuAnchorEl}
        closeMenu={() => setWorkspaceMenuAnchorEl(null)}
        onSelect={(integrationID) => {
          onReauthorizeIntegration(integrationID);
        }}
        shouldShowAddWorkspace={false}
        popoverProps={{
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'right',
          },
        }}
      />
      <AddIntegrationFormDrawer
        isOpen={shouldOpenReauthorizeIntegrationDrawer}
        onDrawerClose={() => {
          setShouldOpenReauthorizeIntegrationDrawer(false);
        }}
        integrationChannel={integrationChannel}
        integrationType={integrationType}
        integrationID={integrationIDToReauthorize}
      />
    </>
  );
};
